import { Sector, getStoredToken, clearAuthData, isTokenExpired } from './auth';
import { parseErrorResponse } from './error';

/**
 * Serviços de ferramentas
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://162.240.235.102:7006';

const jsonHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/**
 * Redireciona para login quando a sessão expira
 */
const handleSessionExpired = () => {
  clearAuthData();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// ==================== INTERFACES ====================

export interface SectorOption {
  value: Sector;
  label: string;
}

export interface Tool {
  id: string;
  name: string;
  description: string;
  icon: string;
  route: string;
  sector: Sector | null;
  comingSoon: boolean;
}

export interface ToolCategory {
  category: string;
  tools: Tool[];
}

export interface AvailableToolsResponse {
  user: {
    email: string;
    roles: string[];
    sector: Sector;
    isAdmin: boolean;
    isStaff: boolean;
  };
  totalTools: number;
  totalAvailableNow: number;
  totalComingSoon: number;
  categories: ToolCategory[];
}

export interface SectorsResponse {
  sectors: SectorOption[];
}

// ==================== TOOLS FUNCTIONS ====================

/**
 * Buscar setores disponíveis (para dropdown de cadastro)
 * GET /api/tools/sectors
 */
export const getSectors = async (): Promise<SectorsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/tools/sectors`, {
    method: 'GET',
    headers: jsonHeaders(),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Buscar ferramentas disponíveis para o usuário logado
 * GET /api/tools/available
 */
export const getAvailableTools = async (): Promise<AvailableToolsResponse> => {
  const token = getStoredToken();
  
  if (!token) {
    handleSessionExpired();
    throw new Error('Usuário não autenticado');
  }

  // Verificar se o token está expirado antes de fazer a requisição
  if (isTokenExpired()) {
    handleSessionExpired();
    throw new Error('Sessão expirada');
  }
  
  const res = await fetch(`${API_BASE_URL}/api/tools/available`, {
    method: 'GET',
    headers: jsonHeaders(token),
  });
  
  if (!res.ok) {
    // Se receber 401, redirecionar para login
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

// ==================== ICON MAPPING ====================

/**
 * Mapeamento de ícones do backend para componentes MUI
 */
export const iconMap: Record<string, string> = {
  dashboard: 'Dashboard',
  person: 'Person',
  payments: 'Payments',
  settings: 'Settings',
  analytics: 'Timeline',
  inventory: 'Inventory',
  people: 'People',
  folder: 'Folder',
  assessment: 'Assessment',
  shopping_cart: 'ShoppingCart',
  business: 'Business',
  support: 'Support',
  code: 'Code',
  security: 'Security',
  email: 'Email',
  calendar: 'CalendarMonth',
  chart: 'BarChart',
  money: 'AttachMoney',
  receipt: 'Receipt',
  account_balance: 'AccountBalance',
};

/**
 * Obter nome do ícone MUI a partir do nome do backend
 */
export const getMuiIconName = (backendIcon: string): string => {
  return iconMap[backendIcon] || 'Apps';
};

